const m = require('minthril');
const { component } = require('../../component');
const { Breadcrumbs } = require('../../components/breadcrumbs');
const { AccountNavigation } = require('../account');
const {
  loadBillingSummary,
  openBillingPortal,
  startBillingCheckout
} = require('../../controllers/billing');

function money(amount, currency) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD'
  }).format(amount || 0);
}

const bytesPerGiB = 1024 ** 3;

function formatNumber(value) {
  return new Intl.NumberFormat().format(value || 0);
}

function formatGiB(bytes) {
  return `${((bytes || 0) / bytesPerGiB).toFixed(3)} GiB`;
}

function formatData(bytes) {
  const megabytes = (bytes || 0) / (1024 ** 2);
  return megabytes < 1024
    ? `${megabytes.toFixed(1)} MB`
    : formatGiB(bytes);
}

function formatDuration(milliseconds) {
  const seconds = (milliseconds || 0) / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} seconds`;
  return `${(seconds / 60).toFixed(1)} minutes`;
}

function formatDate(value) {
  if (!value) return 'Not available yet';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeZone: 'UTC'
  }).format(new Date(value));
}

function billingModeLabel(summary) {
  if (summary.billingMode === 'comped') {
    return 'Comped - This account will never be charged for usage';
  }

  return `Billing mode: ${summary.billingMode || 'free'}`;
}

function costBreakdown(summary) {
  return m('section.billing-page__cost-breakdown', [
    m('h3', 'Cost breakdown'),
    m('div.billing-page__cost-total', [
      m('span', 'Current period cost'),
      m('strong', money(summary.estimatedTotalAmount, summary.currency))
    ]),
    m('h4', 'Cost per site'),
    m('div.billing-page__site-costs', summary.sites.map(site => m('div', [
      m('div.billing-page__site-cost-heading', [
        m('strong', site.name),
        m('strong', money(site.estimatedCost, summary.currency))
      ]),
      m('div', [
        m('span', 'Connected-domain fee'),
        m('span', money(site.connectedDomainCost, summary.currency))
      ]),
      m('div', [
        m('span', 'Usage over included'),
        m('span', money(site.usageOverageCost, summary.currency))
      ])
    ]))),
    m('h4', 'Usage over included allowance'),
    m('div.billing-page__cost-total', [
      m('span', 'Usage overage'),
      m('strong', money(summary.overageAmount, summary.currency))
    ])
  ]);
}

function updateIncludedLabel(marker) {
  const track = marker.dom?.parentNode;
  if (!track) return;
  marker.dom.classList.toggle(
    'billing-page__included-marker--hide-label',
    marker.dom.offsetLeft < 300
  );
}

function usageMeter(label, value, allowance, formatValue, sites, valueForSite) {
  const percentage = allowance ? (value / allowance) * 100 : 0;
  const scale = Math.max(value, allowance, 1);
  const usageWidth = (value / scale) * 100;
  const includedPosition = (allowance / scale) * 100;
  const segmented = sites?.length && valueForSite;
  return m('div.billing-page__meter', [
    m('div.billing-page__meter-heading', [
      m('span', label),
      m('strong', `${formatValue(value)} / ${formatValue(allowance)}`)
    ]),
    m('div.billing-page__meter-track', [
      m(`div.billing-page__meter-fill${segmented ? '.billing-page__meter-fill--segmented' : ''}`, {
        style: { width: `${usageWidth}%` }
      }, segmented && sites.map((site, index) => {
        const siteValue = valueForSite(site);
        const sitePercentage = value ? (siteValue / value) * 100 : 0;
        return m('span.billing-page__share-segment', {
          style: {
            width: `${sitePercentage}%`,
            background: siteShareColors[index % siteShareColors.length]
          },
          title: `${site.name}: ${sitePercentage.toFixed(1)}% · ${formatValue(siteValue)}`,
          'aria-label': `${site.name}: ${sitePercentage.toFixed(1)} percent, ${formatValue(siteValue)}`
        }, m('span.billing-page__share-tooltip', `${site.name}: ${sitePercentage.toFixed(1)}% · ${formatValue(siteValue)}`));
      })),
      m('span.billing-page__included-marker', {
        style: { left: `${includedPosition}%` },
        oncreate: updateIncludedLabel,
        onupdate: updateIncludedLabel,
        title: `Included allowance: ${formatValue(allowance)}`,
        'aria-label': `Included allowance: ${formatValue(allowance)}`
      }, m('span.billing-page__included-label', 'Included'))
    ]),
    m('p.billing-page__meter-caption', `${percentage.toFixed(1)}% consumed`)
  ]);
}

const siteShareColors = [
  'var(--color-primary)',
  'var(--color-secondary)',
  'var(--color-teal)',
  'var(--color-pink)',
  'var(--color-amber)'
];

const BillingPage = component((vnode, emit) => {
  let accountId = null;
  let summary = null;
  let error = null;
  let loading = false;
  let checkingOut = false;
  let openingPortal = false;
  let transactionId = null;

  async function load(user, loadSummary, loadKey) {
    accountId = loadKey;
    loading = true;
    error = null;
    emit('redraw');

    try {
      summary = await loadSummary(user);
    } catch (reason) {
      error = reason.message;
    } finally {
      loading = false;
      emit('redraw');
    }
  }

  async function checkout(user) {
    checkingOut = true;
    error = null;
    transactionId = null;
    emit('redraw');

    try {
      const result = await startBillingCheckout(user);
      if (!result.paymentRequired) {
        summary = { ...summary, status: 'free', amountDue: 0 };
      } else if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
        return;
      } else {
        transactionId = result.transactionId;
      }
    } catch (reason) {
      error = reason.message;
    } finally {
      checkingOut = false;
      emit('redraw');
    }
  }

  async function openPortal(user) {
    openingPortal = true;
    error = null;
    const portalWindow = window.open('about:blank', '_blank');
    if (portalWindow) portalWindow.opener = null;
    emit('redraw');
    try {
      const result = await openBillingPortal(user);
      if (portalWindow) {
        portalWindow.location.href = result.url;
      } else {
        window.location.assign(result.url);
      }
    } catch (reason) {
      portalWindow?.close();
      error = reason.message;
      openingPortal = false;
      emit('redraw');
    }
  }

  return {
    view: ({ attrs: {
      user,
      loadSummary = loadBillingSummary,
      breadcrumbItems = [{ label: 'Billing' }],
      heading = 'Billing',
      allowCheckout = true,
      showAccountNavigation = false,
      loadKey = user.uid
    } }) => {
      if (accountId !== loadKey && !loading) {
        load(user, loadSummary, loadKey);
      }

      return m('section.panel.billing-page', [
        m(Breadcrumbs, { items: breadcrumbItems }),
        showAccountNavigation && m(AccountNavigation, { active: 'billing' }),
        m('h2', heading),
        error && m('p.error', error),
        loading ? m('p.muted', 'Loading billing usage…') : summary && [
          summary.account && m('p.billing-page__account', [
            m('strong', summary.account.displayName || summary.account.email || summary.account.uid),
            summary.account.email && m('span', summary.account.displayName ? ` · ${summary.account.email}` : '')
          ]),
          m('div.billing-page__summary', [
            m('p.billing-page__status', `Account status: ${summary.status}`),
            m('p.billing-page__status', billingModeLabel(summary)),
            summary.subscriptionId && summary.status !== 'canceled' && m('div.billing-page__subscription', [
              m('strong', 'Paid plan active'),
              m('span', `Next payment: ${formatDate(summary.nextPaymentAt)}`),
              summary.cancelAt && m('span', `Plan ends: ${formatDate(summary.cancelAt)}`),
              allowCheckout && summary.customerId && m('button.button.button--quiet', {
                type: 'button',
                disabled: openingPortal,
                onclick: () => openPortal(user)
              }, openingPortal ? 'Opening billing portal…' : 'Manage billing')
            ]),
            m('strong.billing-page__amount', money(summary.estimatedTotalAmount, summary.currency)),
            m('p.muted', `Current period: ${summary.period.start} to ${summary.period.end}`),
            summary.connectedSites > 0 && m('p.muted',
              `${summary.connectedSites} connected ${summary.connectedSites === 1 ? 'site' : 'sites'} · ${money(summary.connectedSiteMonthlyUsd, summary.currency)} per site/month`
            )
          ]),
          costBreakdown(summary),
          m('h3', 'Usage this period'),
          m('div.billing-page__meters', [
            usageMeter(
              'Requests',
              summary.usage.billableProxiedRequests,
              summary.freeTier.billableProxiedRequests,
              formatNumber,
              summary.sites,
              site => site.usage.billableProxiedRequests
            ),
            usageMeter(
              'Data transfer',
              summary.usage.billableResponseBodyBytes,
              summary.freeTier.billableResponseBodyBytes,
              formatData,
              summary.sites,
              site => site.usage.billableResponseBodyBytes
            ),
            usageMeter(
              'Rendering and validation CPU time',
              summary.usage.renderDurationMs + summary.usage.validationDurationMs,
              summary.freeTier.workerDurationMs,
              value => formatDuration(value),
              summary.sites,
              site => site.usage.renderDurationMs + site.usage.validationDurationMs
            )
          ]),
          m('p.billing-page__allowance', `Free allowance remaining: ${formatNumber(Math.max(0, summary.freeTier.billableProxiedRequests - summary.usage.billableProxiedRequests))} requests, ${formatData(Math.max(0, summary.freeTier.billableResponseBodyBytes - summary.usage.billableResponseBodyBytes))} data, and ${formatDuration(Math.max(0, summary.freeTier.workerDurationMs - summary.usage.renderDurationMs - summary.usage.validationDurationMs))} CPU time.`),
          m('div.billing-page__site-legend', summary.sites.map((site, index) => m('span', [
            m('i', { style: { background: siteShareColors[index % siteShareColors.length] } }),
            site.name
          ]))),
          allowCheckout && summary.subscriptionId && summary.status !== 'canceled' && !summary.cancelAt && m('a.button.button--quiet.button--danger', {
            href: '/account/billing/cancel'
          }, 'Cancel plan'),
          allowCheckout && !summary.subscriptionId && summary.billingTestEnabled && summary.amountDue > 0 && m('button.button', {
            type: 'button',
            disabled: checkingOut,
            onclick: () => checkout(user)
          }, checkingOut ? 'Preparing checkout…' : 'Upgrade plan'),
          transactionId && m('p.billing-page__transaction', `Sandbox transaction created: ${transactionId}. Paddle checkout did not return a hosted URL.`),
          summary.amountDue === 0 && summary.overageAmount > 0 && summary.billingMode === 'free' &&
            m('p.muted', 'Your free allowance has been exceeded. Enable paid billing to continue using connected domains.'),
          summary.amountDue === 0 && summary.overageAmount > 0 && summary.billingMode === 'comped' &&
            m('p.muted', 'This account is comped. Usage over the allowance will not be charged.'),
          summary.amountDue === 0 && !summary.overageAmount &&
            m('p.muted', 'You are currently within the free tier.')
        ]
      ]);
    }
  };
});

module.exports = { BillingPage };
